import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import {action, observable} from 'mobx';
import {FormattedMessage} from 'react-intl';
import Mic from 'react-icons/lib/md/mic';
import Plus from 'react-icons/lib/io/plus';
import Bolt from 'react-icons/lib/fa/bolt';
import {actions, category, DialogTypes, events, ProgressPresentationType} from '../../../../constants/enums';
import {BPM_DEFAULT, BPM_MAX, BPM_MIN, pitchShiftArray} from '../../../../constants/constants';
import StudioPlayer from '../../studio/StudioPlayer';
import StudioPlayerSeekCursor from '../../studio/StudioPlayerSeekCursor';
import StudioPlayerTrack from '../../studio/StudioPlayerTrack';
import StudioPlayerCursor from '../../studio/StudioPlayerCursor';
import StudioPlayerTrackGrid from '../../studio/StudioPlayerTrackGrid';
import StudioTopBarsTimeBar from '../../studio/StudioTopBarsTimeBar';
import StudioTopSkipBar from '../../studio/StudioTopSkipBar';
import SvgIconButton from '../../../../components/SvgIconButton';
import Clock from '../../../../assets/images/clock';
import Metronome from '../../../../components/metronome/Metronome';
import StudioSelect from '../../studio/StudioSelect';
import StudioLooperDrawer from '../looper/StudioLooperDrawer';
import {roundBpm} from '../../../../utils/audioUtility';
import StudioPlayerTrackBackgroundLayer from '../../studio/StudioPlayerTrackBackgroundLayer';
import {isGuest} from "../../../../utils/userUtility";
import {customEvent} from "../../../../utils/analyticsUtil";

const tabs = [
    {title: 'time', id: ProgressPresentationType.Time},
    {title: 'beat', id: ProgressPresentationType.Bars},
];

@inject('studioStore', 'looperStore', 'dialogStore')
@observer
class StudioTimeline extends Component {
    @observable activeTab = tabs[0];

    @action handleToggleTab = () => {
        this.activeTab.id = this.activeTab.id === ProgressPresentationType.Time ? ProgressPresentationType.Bars : ProgressPresentationType.Time;
    };

    componentDidMount() {
        this.props.studioStore.updateCurrentTime(this.props.studioStore.leaderTrack);
    }

    toggleMetronome = () => {
        const {looperStore} = this.props;
        if (!looperStore.isActiveMetronome) {
            customEvent(events.CustomEvent, {category: category.MetronomeOn});
        }
        looperStore.toggleMetronome(!looperStore.isActiveMetronome);
    };

    buildBPMList = (min, max) => {
        const list = [];
        for (let i = min; i <= max; i++) {
            list.push({label: i, value: i});
        }
        return list;
    };

    bpmList = this.buildBPMList(BPM_MIN, BPM_MAX);

    handleBPMChange = async ({target}) => {
        const {looperStore} = this.props;
        const bpm = +target.value;
        await looperStore.setProjectBpm(bpm);
        await looperStore.sync(bpm, true);
    };

    handlePitchChange = async ({target}) => {
        customEvent(events.CustomEvent, {category: isGuest() ? category.Discover : category.Studio, action: actions.PitchChange});
        this.props.studioStore.setPitch(+target.value, pitchShiftArray[target.options.selectedIndex].label);
        await this.props.looperStore.sync();
    };

    onEmptyStudioPlusClick = () => {
        const {looperStore, dialogStore} = this.props;
        looperStore.setShowLooperDrawer(true);
        dialogStore.openDialog(DialogTypes.StudioLibrary);
    };

    openRecorderDialog = () => {
        customEvent(events.CustomEvent, {category: isGuest() ? category.Discover : category.Studio, action: actions.Recorder, label: 'Click'});
        this.props.dialogStore.openRecordDialog();
    };

    render() {
        const {looperStore, studioStore, dialogStore} = this.props;
        const {tracks, leaderTrack, looperTracks, handleSkip, pitchLabel, transposeArray} = studioStore;
        const {isPlayingTracks, isActiveMetronome, looperProject, metronomeVolumeChange, metronomeVolume} = looperStore;

        let guest = isGuest();
        return (
            <div className="m-studio-timeline">
                <svg className={`m-svg ${looperTracks.length ? '' : 'empty'}`} id="studioPlayGridSvg" viewBox="0 0 750 750">
                    <g transform="translate(375, 375)">
                        {tracks.map((track, index) => <StudioPlayerTrack
                            key={`StudioPlayerTrack${index}`}
                            tier={index}
                            track={track}
                            mobileView={true}
                        />)}
                        <StudioPlayerTrackBackgroundLayer mobileView={true}/>
                        {looperTracks.length && <StudioPlayerTrackGrid mobileView={true}/>}
                        {(leaderTrack.duration > 0) && <StudioPlayerCursor mobileView={true}/>}
                        {(leaderTrack.duration > 0) && <StudioPlayerSeekCursor mobileView={true} onClick={handleSkip}/>}
                        {looperTracks.length && <StudioPlayer mobileView={true}/>}
                    </g>
                </svg>
                <div className="m-studio-controls">
                    <div className="m-studio-control left">
                        <Metronome
                            isActive={isActiveMetronome} bpm={looperProject.bpm}
                            onClick={this.toggleMetronome} mobileView={true} disabled={!looperTracks.length}
                            onVolumeChange={metronomeVolumeChange} volume={metronomeVolume}
                        />
                        <StudioSelect
                            className="bpm"
                            value={roundBpm(looperProject.bpm) || BPM_DEFAULT}
                            disabled={!looperTracks.length}
                            options={this.bpmList}
                            onChange={this.handleBPMChange}
                            title={<FormattedMessage id="studio.bpm"/>}
                        />
                    </div>
                    <div className="m-studio-control right">
                        <StudioSelect
                            className="pitch"
                            value={looperStore.pitch}
                            label={pitchLabel}
                            disabled={!looperTracks.length}
                            options={transposeArray.slice()}
                            onChange={this.handlePitchChange}
                            title={<FormattedMessage id="studio.pitch"/>}
                        />
                    </div>
                </div>
                <div className="m-studio-bars">
                    <StudioTopBarsTimeBar type={this.activeTab.id} isPlaying={isPlayingTracks}/>
                    <StudioTopSkipBar onSkip={handleSkip}/>
                    <SvgIconButton
                        onClick={this.handleToggleTab}
                        className={`icon-button toggle-bars ${this.activeTab.id === ProgressPresentationType.Time ? 'on' : 'off'}`}
                        viewBox="0 0 24 24"
                        icon={Clock}
                    />
                </div>
                {looperTracks.length
                    ?
                    <SvgIconButton
                        disabled={guest}
                        disableRipple={true}
                        viewBox="0 0 24 24"
                        onClick={this.openRecorderDialog}
                        containerClass={`m-record-btn ${guest ? 'disabled' : ''}`}><Mic/></SvgIconButton>
                    :
                    <div className="m-studio-empty">
                        <div className="m-studio-empty-title">
                            <FormattedMessage id="studio.empty.message"/>
                        </div>
                        <div className="m-studio-empty-btns">
                            <SvgIconButton
                                disabled={guest}
                                disableRipple={true}
                                viewBox="0 0 24 24"
                                onClick={this.onEmptyStudioPlusClick}
                                containerClass="m-upload-btn"><Plus/></SvgIconButton>
                            <SvgIconButton
                                disabled={guest}
                                disableRipple={true}
                                viewBox="0 0 24 24"
                                onClick={dialogStore.openRecordDialog}
                                containerClass="m-record-btn"><Mic/></SvgIconButton>
                        </div>
                    </div>
                }
                <SvgIconButton
                    disabled={guest}
                    disableRipple={true}
                    viewBox="0 0 24 24"
                    onClick={() => dialogStore.openDialog(DialogTypes.StudioAbilities)}
                    containerClass="m-abilities-btn"><Bolt/></SvgIconButton>
                <StudioLooperDrawer/>
            </div>
        );
    }
}

export default StudioTimeline;
